import { Inject, Injectable } from '@angular/core';
import { ConfigBaseFilter, ListSortOption, SimpleConfigBaseFilter } from 'gung-list';
import { ConceptGridCardV2Component, GridViewProductCard, NavigationConfig, Product, ProductListV2ConfigService } from 'gung-standard';
import { map, Observable } from 'rxjs';
import { CultProductGridCardV2Component } from '../components/cult-product-grid-card-v2/cult-product-grid-card-v2.component';

@Injectable({
  providedIn: 'root'
})
export class CultProductListV2ConfigService extends ProductListV2ConfigService {
  isReturnFlow = false;

  constructor(
    @Inject('environment')
    protected environment: NavigationConfig,
  ) {
    super();
    this.gungFlowService.getSelectedFlow().subscribe(flow => {
      if (flow.isReturnOrder) {
        this.isReturnFlow = true;
      } else {
        this.isReturnFlow = false;
      }
    });
  }

  getProductGridCardComponent(item: GridViewProductCard): any {
    if (item.product.productType === 'concept') {
      return ConceptGridCardV2Component;
    } else {
      return CultProductGridCardV2Component;
    }
  }

  getFilters(): ConfigBaseFilter<Product>[] {
    if (this.isReturnFlow) {
      return [];
    }
    const filters = super.getFilters();
    return filters;
  }

  getItems(): Observable<Product[]> {
    return super.getItems().pipe(
      map(products =>
        products.sort((a, b) => {
          if (!a.extra?.pim?.sequence && !b.extra?.pim?.sequence) {
            return 0;
          }
          if (a.extra?.pim?.sequence && !b.extra?.pim?.sequence) {
            return -1;
          }
          if (b.extra?.pim?.sequence && !a.extra?.pim?.sequence) {
            return 1;
          }
          if (Number(a.extra.pim.sequence) > Number(b.extra.pim.sequence)) {
            return -1;
          }
          if (Number(a.extra.pim.sequence) < Number(b.extra.pim.sequence)) {
            return 1;
          }
          return 0;
        })
      )
    );
  }

  getSearchTerms(item: Product) {
    return [item.id, item.name, item.extra.pim?.ar_q_wa_eankod_konsument + ''];
  }

  getBatchSizes(): number[] {
    return [48];
  }

  getSortOptions(): ListSortOption<Product>[] {
    return [
      {
        getLabel: () => 'PIM_SEQUENCE',
        getComparator: () => (item1: Product, item2: Product) =>
          this.sequenceSort(item1?.extra?.pim?.sequence, item2?.extra?.pim?.sequence, -1)
      },
      {
        getLabel: () => 'NAME_ASC',
        getComparator: () => (item1: Product, item2: Product) => this.comparatorHelper(item1?.name, item2?.name, 1)
      },
      {
        getLabel: () => 'NAME_DESC',
        getComparator: () => (item1: Product, item2: Product) => this.comparatorHelper(item1?.name, item2?.name, -1)
      },
      {
        getLabel: () => 'ITEM_NO_ASC',
        getComparator: () => (item1: Product, item2: Product) => this.comparatorHelper(item1?.id, item2?.id, 1)
      },
      {
        getLabel: () => 'ITEM_NO_DESC',
        getComparator: () => (item1: Product, item2: Product) => this.comparatorHelper(item1?.id, item2?.id, -1)
      },
      {
        getLabel: () => 'ITEM_GROUP_ASC',
        getComparator: () => (item1: Product, item2: Product) => this.itemGroupSort(item1, item2, 1)
      },
      {
        getLabel: () => 'ITEM_GROUP_DESC',
        getComparator: () => (item1: Product, item2: Product) => this.itemGroupSort(item1, item2, -1)
      }
    ];
  }

  comparatorHelper(aValue, bValue, ascSort) {
    if (!aValue && !bValue) {
      return 0;
    }
    if (!aValue) {
      return 1 * ascSort;
    }
    if (!bValue) {
      return -1 * ascSort;
    }
    let aNumber;
    let bNumber;
    if (typeof aValue === 'number') {
      aNumber = aValue;
    }
    if (typeof bValue === 'number') {
      bNumber = bValue;
    }
    if (!aNumber && !isNaN(Number(aValue.replace(',', '.')))) {
      aNumber = Number(aValue.replace(',', '.'));
    }
    if (!bNumber && !isNaN(Number(bValue.replace(',', '.')))) {
      bNumber = Number(bValue.replace(',', '.'));
    }
    // Number
    if (aNumber && bNumber) {
      if (Number(aNumber) < Number(bNumber)) {
        return -1 * ascSort;
      }
      if (Number(bNumber) < Number(aNumber)) {
        return 1 * ascSort;
      }
      return 0;
    }
    // String
    return aValue.localeCompare(bValue) * ascSort;
  }

  sequenceSort(aValue, bValue, ascSort) {
    if (!aValue && !bValue) {
      return 0;
    }
    if (!aValue) {
      return 1;
    }
    if (!bValue) {
      return -1;
    }
    let aNumber;
    let bNumber;
    if (typeof aValue === 'number') {
      aNumber = aValue;
    }
    if (typeof bValue === 'number') {
      bNumber = bValue;
    }
    if (!aNumber && !isNaN(Number(aValue.replace(',', '.')))) {
      aNumber = Number(aValue.replace(',', '.'));
    }
    if (!bNumber && !isNaN(Number(bValue.replace(',', '.')))) {
      bNumber = Number(bValue.replace(',', '.'));
    }
    // Number
    if (aNumber && bNumber) {
      return ascSort * (aNumber < bNumber ? -1 : 1);
    }
    // String
    return aValue.localeCompare(bValue) * ascSort;
  }

  itemGroupSort(a, b, ascSort) {
    if (a.extra?.pim?.productGroupCode?.description && b.extra?.pim?.productGroupCode?.description) {
      return (
        a.extra.pim?.productGroupCode?.description.localeCompare(b.extra.pim?.productGroupCode?.description) * ascSort
      );
    }
    if (a.extra?.pim?.productGroupCode?.description) {
      return -1 * ascSort;
    }
    if (b.extra?.pim?.productGroupCode?.description) {
      return 1 * ascSort;
    }
    return 0;
  }
}

export class CultArQEliassonArtnrFilter extends SimpleConfigBaseFilter<Product> {
  type = 'CheckBoxConfigBaseFilter';

  getName(): string {
    return 'CLEARANCE_ARTICLES';
  }

  getOptionIds(item: Product): string[] {
    return [item.extra.ar.q_eliasson_artnr === '50' ? 'CLEARANCE_ARTICLES' : ''];
  }
  getOptionName(key: string): string {
    return key;
  }
}
